<template lang="pug">
.lk-room-page
	header.topbar
		.title CallBridge
		.right
			button.btn.small(v-if="connected" @click="toggleMute") {{ localMuted ? 'Unmute' : 'Mute' }}
			button.btn.small.danger(v-if="connected" @click="leave") Leave
	main.content
		section.status-box(v-if="!connected && !error")
			p Status: {{ status }}
			p(v-if="serverUrl")
				small Server: {{ serverUrl }}
		section.error-box(v-if="error")
			p Error: {{ error }}

		section.participants(v-if="connected")
			h3 Participants ({{ participants.length }})
			ul
				li(
					v-for="p in participants"
					:key="p.sid"
					:class="{ speaking: activeSpeakers.includes(p.identity) }"
				)
					span {{ p.name || p.identity }}
					span.state(:class="{ muted: p.audioMuted }") {{ p.audioMuted ? 'muted' : 'audio' }}
		// Autoplay unlock + debug actions
		.actions(v-if="connected")
			button.btn.small(v-if="playbackBlocked" @click="unlockPlayback") Enable Audio (click)

		.audio-container(ref="audioContainer")
</template>

<script>
// Focus: audio diagnostics only
export default {
	translationCategory: 'livekit.components.LiveKitRoom',
	name: 'LiveKitRoom',
	data() {
		return {
			status: 'Initializing',
			error: '',
			connected: false,
			room: null,
			participants: [],
			activeSpeakers: [],
			localMuted: false,
			livekitClientLibUrl: 'https://cdn.jsdelivr.net/npm/livekit-client/dist/livekit-client.umd.min.js',
			serverUrl: '',
			token: '',
			autoMicTried: false,

			// Analyzer
			anCtx: null,
			anNode: null,
			anSrc: null,
			anRaf: 0,
			anData: null,
			levelState: {
				lastLogTs: 0,
				accRms: 0,
				accPeak: 0,
				samples: 0
			},
			logThrottleMap: Object.create(null),
			lk: null,

			playbackBlocked: false,

			// New diagnostic flags
			rescanAttempts: 0,
			maxRescanAttempts: 5,
			rescanDelaysMs: [150, 500, 1200, 2500, 4000]
		};
	},
	mounted() {
		const q = this.$route.query;
		this.token = q.token;
		this.serverUrl = this.normalizeServerUrl(q.lkUrl || '');
		this.dlog('mount', { hasToken: !!this.token, serverUrl: this.serverUrl });
		if (!this.token) return this.setError('Missing token parameter.');
		if (!this.serverUrl) return this.setError('Missing LiveKit server URL (lkUrl).');
		this.loadSdkAndConnect();

		if (__DEBUG__)
			window.liveKitRoom = this;
	},
	beforeDestroy() {
		this.dlog('beforeDestroy');
		this.stopLocalMicAnalyzer();
		this.leave();
	},
	methods: {
		/* ================= Debug Helpers ================ */
		dlog(event, ...rest) {
			if (typeof __DEBUG__ !== 'undefined' && __DEBUG__) {
				console.debug('[LiveKit][Guest]', event, ...rest);
			}
		},
		throttledLog(key, intervalMs, fn) {
			const now = performance.now();
			const last = this.logThrottleMap[key] || 0;
			if (now - last >= intervalMs) {
				this.logThrottleMap[key] = now;
				fn();
			}
		},

		/* =============== Utility ======================== */
		normalizeServerUrl(raw) {
			this.dlog('normalizeServerUrl', raw);
			if (!raw || typeof raw !== 'string') return '';
			const t = raw.trim();
			if (t.startsWith('wss://') || t.startsWith('ws://')) return t;
			if (t.startsWith('https://')) return 'wss://' + t.slice(8);
			if (t.startsWith('http://')) return 'ws://' + t.slice(7);
			return 'wss://' + t;
		},
		setError(msg) {
			this.dlog('setError', msg);
			this.error = msg;
			this.status = 'Error';
			console.error('[LiveKitRoom]', msg);
		},

		/* =============== SDK Load / Connect ============= */
		loadSdkAndConnect() {
			this.dlog('loadSdkAndConnect');
			this.status = 'Loading SDK';
			if (this.detectClient()) return this.connectRoom();
			const script = document.createElement('script');
			script.src = this.livekitClientLibUrl;
			script.async = true;
			script.onload = () => {
				this.dlog('sdk loaded');
				if (!this.detectClient()) return this.setError('livekit-client global not found after loading script.');
				this.connectRoom();
			};
			script.onerror = () => this.setError('Failed to load livekit-client from CDN.');
			document.head.appendChild(script);
		},
		detectClient() {
			const lk = window.livekitClient || window.LivekitClient || window.livekit || window.LiveKit;
			this.dlog('detectClient', !!lk);
			if (lk) {
				this.lk = lk;
				try { lk.setLogLevel?.('debug'); } catch {}
				return true;
			}
			return false;
		},
		async requestActiveSpeakersUpdates() {
			if (!this.room || !this.room.engine) {
				this.dlog('requestActiveSpeakersUpdates: no room or engine');
				return;
			}
			
			try {
				const engine = this.room.engine;
				
				if (__DEBUG__) {
					console.debug('[LiveKit][Room]: Engine methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(engine)));
				}
				
				// Try different ways to request active speakers
				if (engine.updateSubscription) {
					await engine.updateSubscription();
					this.dlog('engine.updateSubscription() - success');
				}
				
				if (engine.sendUpdateSubscription) {
					const subscription = {
						participantTracks: [],
						allParticipants: true,
						subscribe: true
					};
					await engine.sendUpdateSubscription([subscription]);
					this.dlog('engine.sendUpdateSubscription() - success');
				}
				
			} catch (e) {
				this.dlog('requestActiveSpeakersUpdates error', e);
			}
		},
		async connectRoom() {
			this.dlog('connectRoom: begin');
			this.status = 'Connecting';
			try {
				if (this.lk?.Room) {
					const RoomCls = this.lk.Room;
					this.room = new RoomCls({
						adaptiveStream: true,
						dynacast: true,  // CHANGED: Add dynacast as in official example
					});
					if (this.room.prepareConnection) {
						try {
							await this.room.prepareConnection(this.serverUrl, this.token);
							this.dlog('prepareConnection done');
						} catch (e) {
							this.dlog('prepareConnection non-fatal', e);
						}
					}
					await this.room.connect(this.serverUrl, this.token, { autoSubscribe: true });
				} else if (this.lk?.connect) {
					this.room = await this.lk.connect(this.serverUrl, this.token, {
						autoSubscribe: true,
						adaptiveStream: true,
						dynacast: true,  // CHANGED: Add dynacast
					});
				} else {
					throw new Error('LiveKit SDK missing Room/connect API');
				}
				
				this.dlog('connected', {
					localIdentity: this.room.localParticipant?.identity,
					canPlaybackAudio: this.room.canPlaybackAudio
				});

				this.bindRoomEvents();
				this.attachExistingRemoteAudio(true);
				this.refreshParticipantsList();

				await this.enableInitialMicrophone();
				this.debugEnsureLocalMic();

				this.connected = true;
				this.status = 'Connected';
				this.dlog('connectRoom: success participants=', this.participants.length);

				this.postConnectRescan();
			} catch (err) {
				this.dlog('connectRoom: error', err);
				this.setError('Connect failed: ' + (err.message || err));
			}
		},
		/* =============== Post-Connect Rescan ============ */
		postConnectRescan() {
			this.dlog('postConnectRescan: schedule', this.rescanDelaysMs);
			this.rescanAttempts = 0;
			this.rescanDelaysMs.forEach(delay => {
				setTimeout(() => {
					this.rescanAttempts++;
					this.dlog('postConnectRescan: attempt', this.rescanAttempts);
					this.attachExistingRemoteAudio(false, true);
					this.refreshParticipantsList();
					this.dumpParticipantsState('rescan-' + this.rescanAttempts);
				}, delay);
			});
		},

		/* =============== Microphone Enable ============== */
		async enableInitialMicrophone() {
			if (this.autoMicTried) return;
			this.autoMicTried = true;
			const lp = this.room?.localParticipant;
			if (!lp) return;
			try {
				this.status = 'Enabling microphone...';
				this.dlog('enableInitialMicrophone: setMicrophoneEnabled(true)');
				await lp.setMicrophoneEnabled(true);
				this.localMuted = false;
				this.refreshParticipantsList();
				this.status = 'Connected';
				this.dlog('enableInitialMicrophone: success');
				this.startLocalMicAnalyzer();
			} catch (e) {
				this.localMuted = true;
				this.status = 'Connected (mic blocked)';
				this.dlog('enableInitialMicrophone: failed', e);
			}
		},
		debugEnsureLocalMic(attempt = 1) {
			const lp = this.room?.localParticipant;
			if (!lp) return;
			let pubs = [];
			if (lp.audioTrackPublications instanceof Map) {
				pubs = Array.from(lp.audioTrackPublications.values());
			}
			if (pubs.length && pubs[0].track) {
				this.dlog('debugEnsureLocalMic: track present', { attempt });
				return;
			}
			if (attempt <= 5) {
				this.dlog('debugEnsureLocalMic: retry attempt', attempt);
				lp.setMicrophoneEnabled(true)
					.then(() => {
						this.refreshParticipantsList();
						this.startLocalMicAnalyzer();
					})
					.catch(e => this.dlog('debugEnsureLocalMic: retry fail', e))
					.finally(() => setTimeout(() => this.debugEnsureLocalMic(attempt + 1), 600));
			} else {
				this.dlog('debugEnsureLocalMic: track absent after retries');
			}
		},

		/* =============== Event Binding ================== */
		bindRoomEvents() {
			this.dlog('bindRoomEvents - START');
			
			const RoomEvent = this.lk.RoomEvent;
			
			if (__DEBUG__) {
				console.debug('[LiveKit][Room]: RoomEvent exists:', !!RoomEvent);
				if (RoomEvent) {
					console.debug('[LiveKit][Room]: ActiveSpeakersChanged constant:', RoomEvent.ActiveSpeakersChanged);
				}
			}
			
			// Use the actual RoomEvent constants, not string fallbacks
			this.room
				.on(RoomEvent.ParticipantConnected, p => {
					this.dlog('ParticipantConnected', p.identity);
					this.refreshParticipantsList();
					this.attachExistingRemoteAudio(false, true);
				})
				.on(RoomEvent.ParticipantDisconnected, p => {
					this.dlog('ParticipantDisconnected', p.identity);
					this.refreshParticipantsList();
				})
				.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
					this.dlog('TrackSubscribed', { kind: pub.kind, from: participant.identity, trackSid: track?.sid });
					if (pub.kind === 'audio' && track) {
						this.attachAudioTrack(track, participant);
					}
					this.refreshParticipantsList();
				})
				.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
					this.dlog('TrackUnsubscribed', { kind: pub?.kind, from: participant?.identity, trackSid: track?.sid });
					this.detachAudioTrack(track);
					this.refreshParticipantsList();
				})
				.on(RoomEvent.TrackMuted, (pub, participant) => {
					this.throttledLog('TrackMuted-' + pub.sid, 1000, () => {
						this.dlog('TrackMuted', { sid: pub.sid, from: participant.identity });
					});
					this.refreshParticipantsList();
				})
				.on(RoomEvent.TrackUnmuted, (pub, participant) => {
					this.throttledLog('TrackUnmuted-' + pub.sid, 1000, () => {
						this.dlog('TrackUnmuted', { sid: pub.sid, from: participant.identity });
					});
					this.refreshParticipantsList();
				})
				.on(RoomEvent.LocalTrackUnpublished, (pub, participant) => {
					this.dlog('LocalTrackUnpublished', { sid: pub?.trackSid, source: pub?.source, from: participant.identity });
					this.refreshParticipantsList();
				})
				.on(RoomEvent.ActiveSpeakersChanged, speakers => {
					if (__DEBUG__) {
						console.debug('[LiveKit][Room]: *** ActiveSpeakersChanged event fired ***');
						console.debug('[LiveKit][Room]: Raw speakers array:', speakers);
						console.debug('[LiveKit][Room]: Speakers count:', speakers?.length || 0);
						if (speakers && speakers.length > 0) {
							speakers.forEach((speaker, idx) => {
								console.debug(`[LiveKit][Room]: Speaker ${idx}:`, {
									identity: speaker?.identity,
									sid: speaker?.sid,
									name: speaker?.name || speaker?.identity
								});
							});
						}
					}
					
					const speakerIdentities = (speakers || []).map(s => s?.identity).filter(Boolean);
					this.activeSpeakers = speakerIdentities;
					
					if (__DEBUG__) {
						console.debug('[LiveKit][Room]: Updated activeSpeakers:', this.activeSpeakers);
						console.debug('[LiveKit][Room]: Participants with speaking status:', this.participants.map(p => ({
							identity: p.identity,
							name: p.name,
							speaking: this.activeSpeakers.includes(p.identity)
						})));
					}
					
					this.throttledLog('ActiveSpeakersChanged', 500, () => {
						this.dlog('ActiveSpeakersChanged processed:', this.activeSpeakers);
					});
				})
				.on(RoomEvent.AudioPlaybackStatusChanged, () => {
					const allowed = this.room.canPlaybackAudio;
					this.dlog('AudioPlaybackStatusChanged', { allowed });
					this.playbackBlocked = !allowed;
				})
				.on(RoomEvent.Disconnected, () => {
					this.dlog('Room Disconnected');
					this.connected = false;
					this.status = 'Disconnected';
					this.stopLocalMicAnalyzer();
					this.activeSpeakers = [];
				});
				
			this.dlog('bindRoomEvents - COMPLETE');
		},

		/* =============== Participants Helpers =========== */
		iterParticipants() {
			if (!this.room) return [];
			const ps = this.room.remoteParticipants;
			if (!ps) return [];
			if (ps instanceof Map) return Array.from(ps.values());
			if (Array.isArray(ps)) return ps;
			return Object.values(ps);
		},
		getAudioPublications(participant) {
			if (!participant) return [];
			if (participant.audioTrackPublications instanceof Map) {
				return Array.from(participant.audioTrackPublications.values());
			}
			if (participant.tracks instanceof Map) {
				const pubs = [];
				participant.tracks.forEach(pub => pub?.kind === 'audio' && pubs.push(pub));
				return pubs;
			}
			if (Array.isArray(participant.tracks)) {
				return participant.tracks.filter(pub => pub && pub.kind === 'audio');
			}
			return [];
		},
		isAudioMuted(participant) {
			if (this.room?.localParticipant && participant === this.room.localParticipant && this.localMuted === false) {
				return false;
			}
			const pubs = this.getAudioPublications(participant);
			if (!pubs.length) return true;
			return pubs.every(pub => {
				const track = pub.track || pub.audioTrack;
				const noActive = !track || track.muted === true || track.isMuted === true;
				return pub.muted === true || noActive;
			});
		},
		refreshParticipantsList() {
			this.throttledLog('refreshParticipantsList', 500, () => {
				this.dlog('refreshParticipantsList');
			});
			if (!this.room) {
				this.participants = [];
				return;
			}
			const list = [];
			for (const p of this.iterParticipants()) {
				list.push({
					sid: p.sid,
					identity: p.identity,
					name: p.name,
					audioMuted: this.isAudioMuted(p)
				});
			}
			if (this.room.localParticipant) {
				const lp = this.room.localParticipant;
				const idx = list.findIndex(x => x.sid === lp.sid);
				const localEntry = {
					sid: lp.sid,
					identity: lp.identity,
					name: lp.name || 'Me',
					audioMuted: this.isAudioMuted(lp)
				};
				if (idx >= 0) list.splice(idx, 1);
				list.unshift(localEntry);
			}
			this.participants = list;
		},

		/* =============== Attach Existing Remote Audio === */
		attachExistingRemoteAudio(initial = false, verbose = false) {
			if (!this.room) return;
			const tag = initial ? 'initialScan' : 'scan';
			const parts = this.iterParticipants().filter(p => p !== this.room.localParticipant);
			this.dlog('attachExistingRemoteAudio', { tag, remoteCount: parts.length });
			for (const p of parts) {
				const pubs = this.getAudioPublications(p);
				if (verbose) {
					this.dlog('remoteParticipantState', {
						identity: p.identity,
						pubCount: pubs.length,
						pubInfo: pubs.map(pub => ({
							sid: pub.sid,
							isSubscribed: pub.isSubscribed,
							muted: pub.muted,
							hasTrack: !!pub.track
						}))
					});
				}
				for (const pub of pubs) {
					if (pub.isSubscribed && pub.track) {
						const existingEl = document.getElementById('lk-audio-' + pub.track.sid);
						if (!existingEl) {
							this.dlog('attachExistingRemoteAudio: attaching missed track', {
								trackSid: pub.track.sid,
								from: p.identity
							});
							this.attachAudioTrack(pub.track, p);
						}
					}
				}
			}
		},

		/* =============== Audio Elements ================= */
		attachAudioTrack(track, participant) {
			const elId = `lk-audio-${track.sid}`;
			if (document.getElementById(elId)) return;
			this.dlog('attachAudioTrack', { sid: track.sid, participant: participant.identity });
			const audioEl = document.createElement('audio');
			audioEl.id = elId;
			audioEl.autoplay = true;
			audioEl.playsInline = true;
			audioEl.dataset.participant = participant.identity;
			audioEl.muted = false;
			audioEl.onplay = () => this.dlog('audio element play', { sid: track.sid, from: participant.identity });
			audioEl.onpause = () => this.dlog('audio element pause', { sid: track.sid });
			audioEl.onvolumechange = () => this.dlog('audio element volumechange', {
				sid: track.sid,
				vol: audioEl.volume,
				muted: audioEl.muted
			});
			track.attach(audioEl);
			this.$refs.audioContainer.appendChild(audioEl);
			audioEl.play().catch(err => {
				this.dlog('attachAudioTrack play blocked', err?.message);
				this.playbackBlocked = true;
			});
		},
		detachAudioTrack(track) {
			this.dlog('detachAudioTrack', { sid: track?.sid });
			try {
				const els = track?.detach?.();
				if (Array.isArray(els)) els.forEach(el => el.remove());
			} catch (e) {
				console.warn('Failed to detach track', e);
			}
		},

		/* =============== Autoplay Unlock =============== */
		unlockPlayback() {
			if (!this.room) return;
			this.dlog('unlockPlayback: startAudio()');
			this.room.startAudio()
				.then(() => {
					this.dlog('unlockPlayback: success canPlaybackAudio=', this.room.canPlaybackAudio);
					this.playbackBlocked = !this.room.canPlaybackAudio;
				})
				.catch(e => this.dlog('unlockPlayback: failed', e));
		},

		/* =============== Diagnostics ==================== */
		dumpParticipantsState(label = 'dump') {
			if (!this.room) {
				this.dlog('dumpParticipantsState', label, 'no room');
				return;
			}
			const data = [];
			const localId = this.room.localParticipant?.identity;
			for (const p of this.iterParticipants()) {
				const pubs = this.getAudioPublications(p);
				data.push({
					identity: p.identity,
					isLocal: p.identity === localId,
					audioPubCount: pubs.length,
					audio: pubs.map(pub => ({
						sid: pub.sid,
						isSubscribed: pub.isSubscribed,
						muted: pub.muted,
						hasTrack: !!pub.track,
						trackMuted: pub.track?.muted,
						attachedElements: (pub.track?.attachedElements || []).length
					}))
				});
			}
			this.dlog('dumpParticipantsState', label, data);
		},
		debugDumpParticipants() {
			this.dumpParticipantsState('manual');
		},

		/* =============== Analyzer ======================= */
		startLocalMicAnalyzer() {
			if (this.anRaf) return;
			const lp = this.room?.localParticipant;
			if (!lp) return;
			let pub = null;
			if (lp.audioTrackPublications instanceof Map) {
				pub = Array.from(lp.audioTrackPublications.values())[0];
			}
			const track = pub?.track;
			const mediaTrack = track?.mediaStreamTrack;
			if (!mediaTrack) {
				this.dlog('startLocalMicAnalyzer: mediaTrack missing (retry later)');
				return;
			}
			try {
				this.anCtx = this.anCtx || new (window.AudioContext || window.webkitAudioContext)();
				const stream = new MediaStream([mediaTrack]);
				this.anSrc = this.anCtx.createMediaStreamSource(stream);
				this.anNode = this.anCtx.createAnalyser();
				this.anNode.fftSize = 512;
				this.anData = new Uint8Array(this.anNode.fftSize);
				this.anSrc.connect(this.anNode);
				Object.assign(this.levelState, {
					lastLogTs: performance.now(),
					accPeak: 0,
					accRms: 0,
					samples: 0
				});
				this.dlog('startLocalMicAnalyzer: started');

				const loop = () => {
					this.anRaf = requestAnimationFrame(loop);
					if (!this.anNode) return;
					this.anNode.getByteTimeDomainData(this.anData);
					let sum = 0;
					let peak = 0;
					for (let i = 0; i < this.anData.length; i++) {
						const v = (this.anData[i] - 128) / 128;
						const av = Math.abs(v);
						sum += v * v;
						if (av > peak) peak = av;
					}
					const rms = Math.sqrt(sum / this.anData.length);
					const st = this.levelState;
					st.accRms += rms;
					st.accPeak = Math.max(st.accPeak, peak);
					st.samples++;
					const now = performance.now();
					if (now - st.lastLogTs >= 1000) {
						const avgRms = st.accRms / (st.samples || 1);
						st.lastLogTs = now;
						st.accRms = 0;
						st.accPeak = 0;
						st.samples = 0;
					}
				};
				this.anRaf = requestAnimationFrame(loop);
			} catch (e) {
				this.dlog('startLocalMicAnalyzer error', e);
			}
		},
		stopLocalMicAnalyzer() {
			if (this.anRaf) cancelAnimationFrame(this.anRaf);
			this.anRaf = 0;
			try { this.anSrc?.disconnect(); } catch {}
			try { this.anNode?.disconnect(); } catch {}
			this.anSrc = null;
			this.anNode = null;
			this.anData = null;
			this.dlog('stopLocalMicAnalyzer');
		},

		/* =============== User Actions =================== */
		toggleMute() {
			this.dlog('toggleMute', { current: this.localMuted });
			if (!this.room?.localParticipant) return;
			const lp = this.room.localParticipant;
			let micPub = null;
			if (lp.audioTrackPublications instanceof Map) {
				micPub = Array.from(lp.audioTrackPublications.values())[0];
			} else if (lp.tracks instanceof Map) {
				lp.tracks.forEach(pub => { if (!micPub && pub.kind === 'audio') micPub = pub; });
			} else if (Array.isArray(lp.tracks)) {
				micPub = lp.tracks.find(pub => pub.kind === 'audio');
			}
			if (micPub?.track) {
				if (this.localMuted) {
					this.dlog('toggleMute -> unmute');
					micPub.track.unmute?.();
					this.localMuted = false;
				} else {
					this.dlog('toggleMute -> mute');
					micPub.track.mute?.();
					this.localMuted = true;
				}
				this.refreshParticipantsList();
				return;
			}
			lp.setMicrophoneEnabled(!this.localMuted)
				.then(() => {
					this.localMuted = !this.localMuted ? false : true;
					this.dlog('toggleMute -> setMicrophoneEnabled result', this.localMuted);
					this.refreshParticipantsList();
					this.startLocalMicAnalyzer();
				})
				.catch(e => this.setError('Failed to enable microphone: ' + e.message));
		},
		async leave() {
			this.dlog('leave');
			try { await this.room?.disconnect(); } catch {}
			this.connected = false;
			this.status = 'Left';
			this.stopLocalMicAnalyzer();
		}
	}
};
</script>

<style lang="scss" scoped>
.lk-room-page {
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	background: #141414;
	color: #eee;
	font-family: system-ui, sans-serif;
}
.topbar {
	height: 48px;
	background: #1f1f26;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 16px;
	border-bottom: 1px solid #2a2a33;
	.title { font-weight: 600; }
	.right { display: flex; gap: 8px; }
}
.content {
	flex: 1;
	overflow: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 24px;
}
.status-box,
.error-box {
	background: #23232b;
	padding: 16px;
	border-radius: 8px;
	font-size: 14px;
}
.error-box {
	border: 1px solid #613030;
	color: #ff6d6d;
}
.participants {
	background: #1d1d24;
	padding: 16px;
	border-radius: 8px;
	h3 { margin: 0 0 12px; font-size: 15px; }
	ul {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 6px;
		li {
			display: flex;
			justify-content: space-between;
			background: #262630;
			padding: 8px 10px;
			border-radius: 6px;
			border: 2px solid transparent;
			transition: all 0.2s ease-in-out;
			.state {
				font-size: 11px;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				&.muted { color: #ff6d6d; }
			}
			&.speaking {
				border-color: #4caf50;
				box-shadow: 0 0 8px rgba(76, 175, 80, 0.7);
			}
		}
	}
}
.actions {
	display: flex;
	gap: 12px;
}
.audio-container {
	position: absolute;
	left: -9999px;
	top: -9999px;
	width: 0;
	height: 0;
	overflow: hidden;
}
.btn {
	background: #34343e;
	border: 1px solid #454555;
	color: #eee;
	padding: 6px 14px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 13px;
	transition: background 0.2s;
	&:hover { background: #40404a; }
	&.danger {
		background: #5a2a2a;
		border-color: #703838;
		&:hover { background: #6a3030; }
	}
	&.small {
		padding: 4px 10px;
		font-size: 12px;
	}
}
</style>